import React, { useEffect, useState } from 'react';
import { ArrowBack } from "@mui/icons-material";
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';

const TIPOS: { value: string; label: string }[] = [
  { value: 'bacia', label: 'Bacia' },
  { value: 'rio', label: 'Rio' },
  { value: 'represa', label: 'Represa' },
  { value: 'trecho_municipio', label: 'Trecho por município' },
  { value: 'area_pesca', label: 'Área de pesca específica' },
  { value: 'outro', label: 'Outro' },
];

type Modo = null | 'existente' | 'novo';

const PendenciasLocais = () => {
  const [pendencias, setPendencias] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [modoAberto, setModoAberto] = useState<Record<string, Modo>>({});
  const [uidDestinoEscolhido, setUidDestinoEscolhido] = useState<Record<string, string>>({});
  const [buscaDestino, setBuscaDestino] = useState<Record<string, string>>({});
  const [resultadosBusca, setResultadosBusca] = useState<Record<string, any[]>>({});
  const [nomeNovo, setNomeNovo] = useState<Record<string, string>>({});
  const [tipoNovo, setTipoNovo] = useState<Record<string, string>>({});
  const [resolvendo, setResolvendo] = useState<Record<string, boolean>>({});
  const [erro, setErro] = useState<Record<string, string>>({});

  const fetchPendencias = () => {
    setCarregando(true);
    api.get('/pesca/locais/pendencias')
      .then((res) => setPendencias(res.data.data))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { fetchPendencias(); }, []);

  const abrirModo = (chave: string, modo: Modo, pendencia: any) => {
    setModoAberto((prev) => ({ ...prev, [chave]: prev[chave] === modo ? null : modo }));
    if (modo === 'novo' && !nomeNovo[chave]) {
      setNomeNovo((prev) => ({ ...prev, [chave]: pendencia.nomeSugerido }));
      setTipoNovo((prev) => ({ ...prev, [chave]: pendencia.tipoSugerido || 'outro' }));
    }
  };

  const buscarDestino = (chave: string, texto: string) => {
    setBuscaDestino((prev) => ({ ...prev, [chave]: texto }));
    if (texto.trim().length < 2) {
      setResultadosBusca((prev) => ({ ...prev, [chave]: [] }));
      return;
    }
    api.get(`/pesca/locais?search=${encodeURIComponent(texto.trim())}`)
      .then((res) => setResultadosBusca((prev) => ({ ...prev, [chave]: res.data.data })));
  };

  const resolverComExistente = async (pendencia: any) => {
    const uidDestino = uidDestinoEscolhido[pendencia.chave];
    if (!uidDestino) return;
    setResolvendo((prev) => ({ ...prev, [pendencia.chave]: true }));
    setErro((prev) => ({ ...prev, [pendencia.chave]: '' }));
    try {
      await api.post('/pesca/locais/pendencias/resolver', {
        textosNormalizados: pendencia.textosNormalizados,
        acao: 'existente',
        uid_pesca_local_destino: uidDestino,
      });
      setPendencias((prev) => prev.filter((p) => p.chave !== pendencia.chave));
    } catch (err: any) {
      setErro((prev) => ({ ...prev, [pendencia.chave]: err?.response?.data?.message || 'Erro ao resolver.' }));
    } finally {
      setResolvendo((prev) => ({ ...prev, [pendencia.chave]: false }));
    }
  };

  const resolverComNovo = async (pendencia: any) => {
    const nome = nomeNovo[pendencia.chave]?.trim();
    const tipo = tipoNovo[pendencia.chave];
    if (!nome || !tipo) return;
    setResolvendo((prev) => ({ ...prev, [pendencia.chave]: true }));
    setErro((prev) => ({ ...prev, [pendencia.chave]: '' }));
    try {
      await api.post('/pesca/locais/pendencias/resolver', {
        textosNormalizados: pendencia.textosNormalizados,
        acao: 'novo',
        nome,
        tipo,
      });
      setPendencias((prev) => prev.filter((p) => p.chave !== pendencia.chave));
    } catch (err: any) {
      setErro((prev) => ({ ...prev, [pendencia.chave]: err?.response?.data?.message || 'Erro ao resolver.' }));
    } finally {
      setResolvendo((prev) => ({ ...prev, [pendencia.chave]: false }));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Pendências de Local</h1>
        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
            Qtd: <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{pendencias.length}</span>
          </p>
          <button className={styles.blueBtn} onClick={() => window.history.back()} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-8px', marginBottom: '16px' }}>
        Textos de local que os pescadores informaram e o sistema ainda não conseguiu reconhecer automaticamente.
        Uma vez resolvida, cada variação nunca mais aparece aqui.
      </p>

      {carregando ? (
        <p style={{ color: '#999' }}>Carregando...</p>
      ) : pendencias.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '30px', textAlign: 'center', color: '#999' }}>
          Nenhuma pendência no momento.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {pendencias.map((p) => (
            <div key={p.chave} style={{ background: '#fff', borderRadius: '10px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <strong style={{ fontSize: '0.95rem' }}>{p.nomeSugerido}</strong>
                  <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#999' }}>
                    {p.quantidadeRegistros} registro(s) · {p.textosNormalizados.length} variação(ões)
                  </span>
                  <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '4px' }}>
                    Exemplos: {p.textosOriginaisExemplo.join(' · ')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button style={smallBtnStyle(modoAberto[p.chave] === 'existente')} onClick={() => abrirModo(p.chave, 'existente', p)}>
                    É alias de...
                  </button>
                  <button style={smallBtnStyle(modoAberto[p.chave] === 'novo')} onClick={() => abrirModo(p.chave, 'novo', p)}>
                    É lugar novo
                  </button>
                </div>
              </div>

              {modoAberto[p.chave] === 'existente' && (
                <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
                  {p.sugestoesNos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                      {p.sugestoesNos.map((s: any) => (
                        <button
                          key={s.uid_pesca_local}
                          onClick={() => setUidDestinoEscolhido((prev) => ({ ...prev, [p.chave]: s.uid_pesca_local }))}
                          style={chipStyle(uidDestinoEscolhido[p.chave] === s.uid_pesca_local)}
                        >
                          {s.nome} <span style={{ opacity: 0.6 }}>({s.tipo}{s.paiNome ? ` — ${s.paiNome}` : ''})</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <input
                    style={inputStyle}
                    placeholder="Ou busque outro local..."
                    value={buscaDestino[p.chave] || ''}
                    onChange={(e) => buscarDestino(p.chave, e.target.value)}
                  />
                  {(resultadosBusca[p.chave] || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {resultadosBusca[p.chave].map((l: any) => (
                        <button
                          key={l.uid_pesca_local}
                          onClick={() => setUidDestinoEscolhido((prev) => ({ ...prev, [p.chave]: l.uid_pesca_local }))}
                          style={chipStyle(uidDestinoEscolhido[p.chave] === l.uid_pesca_local)}
                        >
                          {l.nome} <span style={{ opacity: 0.6 }}>({l.tipo}{l.pai ? ` — ${l.pai.nome}` : ''})</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                    <button
                      style={primaryBtnStyle}
                      disabled={!uidDestinoEscolhido[p.chave] || resolvendo[p.chave]}
                      onClick={() => resolverComExistente(p)}
                    >
                      {resolvendo[p.chave] ? 'Resolvendo...' : 'Confirmar'}
                    </button>
                  </div>
                </div>
              )}

              {modoAberto[p.chave] === 'novo' && (
                <div style={{ marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '12px', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>Nome</label>
                    <input
                      style={inputStyle}
                      value={nomeNovo[p.chave] ?? p.nomeSugerido}
                      onChange={(e) => setNomeNovo((prev) => ({ ...prev, [p.chave]: e.target.value }))}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Tipo</label>
                    <select
                      style={inputStyle}
                      value={tipoNovo[p.chave] ?? (p.tipoSugerido || 'outro')}
                      onChange={(e) => setTipoNovo((prev) => ({ ...prev, [p.chave]: e.target.value }))}
                    >
                      {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <button style={primaryBtnStyle} disabled={resolvendo[p.chave]} onClick={() => resolverComNovo(p)}>
                    {resolvendo[p.chave] ? 'Criando...' : 'Criar e vincular'}
                  </button>
                </div>
              )}

              {erro[p.chave] && <p style={{ color: '#f75a68', fontSize: '0.82rem', marginTop: '8px' }}>{erro[p.chave]}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.78rem', color: '#666', marginBottom: '4px' };
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 18px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
};
const smallBtnStyle = (ativo: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: '16px', fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap',
  border: ativo ? '2px solid #03c9fe' : '1px solid #ddd', background: ativo ? '#eaf9ff' : '#fff', color: '#333',
});
const chipStyle = (selecionado: boolean): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: '16px', fontSize: '0.8rem', cursor: 'pointer',
  border: selecionado ? '2px solid #00b37e' : '1px solid #ddd', background: selecionado ? '#e6fbf3' : '#fafafa', color: '#333',
});

export default PendenciasLocais;
